import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../../services/toastService'
import { adminAPI, uploadAPI } from '../../services/api'
import { createLogger } from '../../utils/logger'
import { trackAdminEntityVerify } from '../../utils/analytics'

const logger = createLogger('EntityDetailActions')

/**
 * Custom hook for EntityDetail page actions
 * Extracts all handler logic to reduce component size
 * 
 * @param {Object} params
 * @param {string} params.entityId - Current entity ID
 * @param {boolean} params.isEditMode - Whether editing existing entity or creating new
 * @param {Object} params.entity - Entity data
 * @param {Function} params.loadEntity - Function to reload entity data
 * @param {Function} params.executeEntity - useAsyncOperation execute function
 * @param {Object} params.formData - Form data state
 * @param {Function} params.setFormData - Form data setter
 * @param {Function} params.setMapPosition - Map position setter
 * @param {Function} params.setUploadingImage - Uploading image setter
 * @param {Function} params.setEditing - Editing state setter
 * @param {Function} params.setValidationErrors - Validation errors setter
 * @param {Function} params.validateForm - Form validation function
 * @returns {Object} Handler functions
 */
export function useEntityDetailActions({
  entityId,
  isEditMode,
  entity,
  loadEntity,
  executeEntity,
  formData,
  setFormData,
  setMapPosition,
  setUploadingImage,
  setEditing,
  setValidationErrors,
  validateForm
}) {
  const navigate = useNavigate()

  // Image upload handler
  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name} is too large. Max 5MB per image.`)
      return
    }
    
    setUploadingImage(true)
    try {
      const uploadResponse = await uploadAPI.uploadImage(file)
      setFormData(prev => ({ ...prev, imageUrl: uploadResponse.data.url }))
      toast.success('Image uploaded successfully')
    } catch (error) {
      logger.error('Failed to upload image', error)
      toast.error('Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }, [setFormData, setUploadingImage])

  // Map position change handler
  const handleMapPositionChange = useCallback((newPosition) => {
    if (newPosition) {
      setMapPosition(newPosition)
      setFormData(prev => ({
        ...prev,
        currentLatitude: newPosition.lat,
        currentLongitude: newPosition.lng
      }))
    }
  }, [setFormData, setMapPosition])

  // Save entity (create or update)
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors before saving')
      return
    }

    await executeEntity(async () => {
      if (isEditMode) {
        await adminAPI.updateEntity(entityId, formData)
        toast.success('Entity updated successfully')
        setEditing(false)
        loadEntity()
      } else {
        const entityData = { ...formData }
        const imageUrl = entityData.imageUrl
        entityData.imageUrl = ''
        
        const response = await adminAPI.createEntity(entityData)
        const newEntityId = response.data.data.id
        
        const hasValidCoordinates = 
          formData.currentLatitude !== null && 
          formData.currentLatitude !== '' && 
          !isNaN(formData.currentLatitude) &&
          formData.currentLongitude !== null && 
          formData.currentLongitude !== '' && 
          !isNaN(formData.currentLongitude)
        
        if (hasValidCoordinates) {
          try {
            const sightingData = {
              entityId: newEntityId,
              latitude: parseFloat(formData.currentLatitude),
              longitude: parseFloat(formData.currentLongitude),
              address: formData.currentAddress || null,
              imageUrl: imageUrl || null,
              notes: 'Initial location (created by admin)',
              sightedAt: new Date().toISOString()
            }
            await adminAPI.createSighting(sightingData)
          } catch (error) {
            logger.error('Failed to create initial sighting', error)
            toast.warning('Entity created but initial sighting could not be created')
          }
        }
        
        toast.success('Entity created successfully')
        navigate('/admin/entities')
      }
    }, { errorMessage: isEditMode ? 'Failed to update entity' : 'Failed to create entity' })
  }, [validateForm, executeEntity, isEditMode, entityId, formData, setEditing, loadEntity, navigate])

  // Delete entity
  const handleDelete = useCallback(async () => {
    if (!confirm('Are you sure you want to delete this entity? This action cannot be undone.')) {
      return
    }
    
    await executeEntity(async () => {
      await adminAPI.deleteEntity(entityId)
      toast.success('Entity deleted successfully')
      navigate('/admin/entities')
    }, { errorMessage: 'Failed to delete entity' })
  }, [executeEntity, entityId, navigate])

  // Verify entity
  const handleVerify = useCallback(async () => {
    try {
      await adminAPI.verifyEntity(entityId)
      trackAdminEntityVerify(entityId, entity?.name, true)
      toast.success('Entity verified successfully')
      loadEntity()
    } catch (error) {
      logger.error('Failed to verify entity', error)
      toast.error('Failed to verify entity')
    }
  }, [entityId, entity, loadEntity])

  // Cancel editing
  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setEditing(false)
      setFormData({
        name: entity?.name || '',
        entityType: entity?.type || 'food_truck',
        category: entity?.category || '',
        description: entity?.description || '',
        email: entity?.email || '',
        phone: entity?.phone || '',
        website: entity?.website || '',
        imageUrl: entity?.imageUrl || '',
        currentAddress: entity?.currentAddress || '',
        currentLatitude: entity?.currentLatitude || null,
        currentLongitude: entity?.currentLongitude || null,
        isActive: entity?.isActive ?? true
      })
      setValidationErrors({})
    } else {
      navigate('/admin/entities')
    }
  }, [isEditMode, entity, setEditing, setFormData, setValidationErrors, navigate])

  return {
    handleImageUpload,
    handleMapPositionChange,
    handleSave,
    handleDelete,
    handleVerify,
    handleCancel
  }
}
